// Columna central del explorer: barra de stock, métricas, la tabla con el `DataTable` compartido y
// el pie con el contador de lo cargado. Toma el lenguaje visual de Inventario del Admin VL.
import type { Table } from '@tanstack/react-table';
import { Badge, ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { KindChip } from '@/shared/patterns/KindChip';
import {
    LoadedScopeFooter,
    loadedCountLabel,
    loadedCountTitle,
    loadedCountsNote,
    PRODUCTS_NOUN,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { PromotionPill } from '@/shared/patterns/Promotions';
import { StatsBar, type StatsBarItem } from '@/shared/patterns/StatsBar';
import { StockAlertChip } from '@/shared/patterns/StockAlertChip';
import { TableToolbar, ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { isService } from '@/domains/sales/wizard/catalogAvailability';
import type { PortalCatalogRow } from '@/api/services/retail-wizard/contract';
import { financierChips } from '../../pos/lib/financierChips';
import {
    catalogAlerts,
    catalogKindOf,
    catalogStats,
    hasPromotion,
    nextSort,
    productLabel,
    productVariant,
    stockLabelOf,
    stockTone,
    stockToneOf,
    type CatalogMetrics,
    type CatalogStats,
    type SortKey,
    type SortState,
    type StatFilter,
} from '../lib/catalogExplorer';
import type { CatalogListErrorCopy } from '../lib/catalogList';
import {
    variantCountLabel,
    modelTableRows,
    priceRangeLabel,
    rowSku,
    SOME_VARIANTS_LABEL,
    variantCoverage,
    type CatalogEntry,
    type ModelTableRow,
} from '../lib/modelGroups';
import { useExpandedModels } from '../hooks/useExpandedModels';
import {
    groupFinanciers,
    ModelNameCell,
    VARIANT_ROW_CLASS,
    VariantNameCell,
} from './ModelRowCells';
import { skuPromotionSummaries, undetailedPromotionReason } from '../lib/catalogPromotions';

/** Guion del dato que no se puede afirmar. Un cero afirmaría una cantidad. */
const PENDING_CELL = '—';

const STOCK_CLASS: Record<ReturnType<typeof stockTone>, string> = {
    ok: 'text-foreground',
    low: 'text-warning',
    out: 'text-danger',
};

interface StatPill {
    key: StatFilter;
    label: string;
    tone: StatsBarItem['tone'];
    hint?: string;
}

/** Las píldoras de Inventario, en su orden y color, más la de promoción del vendedor. */
const STAT_PILLS: StatPill[] = [
    { key: 'all', label: 'Todos', tone: 'neutral' },
    {
        key: 'inStock',
        label: 'Con stock',
        tone: 'success',
        hint: 'Productos con piezas en tu tienda',
    },
    { key: 'low', label: 'Stock bajo', tone: 'danger', hint: 'De 1 a 10 piezas en tu tienda' },
    { key: 'out', label: 'Sin stock', tone: 'muted', hint: 'Sin piezas en tu tienda' },
    { key: 'promo', label: 'Promo', tone: 'primary', hint: 'Con promoción vigente' },
];

/** La alerta de stock de la fila; como en Inventario, «Top ventas» no es alerta de la tabla. */
function stockAlertOf(row: PortalCatalogRow) {
    return catalogAlerts(row).find((alert) => alert.key !== 'top') ?? null;
}

function AlertsCell({ row }: { row: PortalCatalogRow }) {
    const alert = stockAlertOf(row);
    if (alert === null) return <span className="text-muted-foreground/60">·</span>;
    return (
        <StockAlertChip alert={alert.key} className="truncate">
            {alert.label}
        </StockAlertChip>
    );
}

type CatalogRow = ModelTableRow<PortalCatalogRow>;

/** La fila del modelo se pinta como su variante por defecto con el stock de todo el modelo. */
function modelSummaryRow(row: Extract<CatalogRow, { kind: 'model' }>): PortalCatalogRow {
    return { ...row.group.defaultVariant, stock: row.group.stockTotal };
}

/** Firma de las promos de un SKU, para saber si el modelo las comparte en todas sus variantes. */
function promotionSignature(item: PortalCatalogRow): string {
    if (!hasPromotion(item)) return '';
    const names = skuPromotionSummaries(item).map((summary) => summary.name);
    return names.length > 0 ? names.join('|') : 'undetailed';
}

/** Promo de la fila del modelo: la común a todas sus variantes, o «En algunas variantes». */
function ModelPromotionCell({ row }: { row: Extract<CatalogRow, { kind: 'model' }> }) {
    const coverage = variantCoverage(row.group, promotionSignature);
    if (coverage.kind === 'some') {
        return <span className="text-[10px] text-muted-foreground">{SOME_VARIANTS_LABEL}</span>;
    }
    return (
        <PromotionCell row={coverage.kind === 'all' ? coverage.sample : row.group.defaultVariant} />
    );
}

/** Columnas en una línea; los anchos suman el 100 % y caben en la columna a 1440 px. */
function catalogColumns(onToggle: (key: string) => void): DataTableColumn<CatalogRow, SortKey>[] {
    return [
        {
            key: 'name',
            label: 'Nombre',
            width: '20%',
            sortKey: 'brand',
            cell: (row) =>
                row.kind === 'model' ? (
                    <ModelNameCell
                        group={row.group}
                        expanded={row.expanded}
                        onToggle={() => {
                            onToggle(row.key);
                        }}
                    />
                ) : row.kind === 'variant' ? (
                    <VariantNameCell item={row.item} />
                ) : (
                    <span className="font-semibold">{productLabel(row.item)}</span>
                ),
            cellTitle: (row) =>
                row.kind === 'model'
                    ? `${row.group.brand} ${row.group.model}`
                    : [productLabel(row.item), productVariant(row.item)]
                          .filter(Boolean)
                          .join(' · '),
        },
        {
            key: 'sku',
            label: 'SKU',
            width: '10.5%',
            sortKey: 'sku',
            cell: (row) =>
                row.kind === 'model' ? (
                    <span className="text-[10.5px] text-muted-foreground">
                        {variantCountLabel(row.group)}
                    </span>
                ) : (
                    <span className="font-mono text-[10.5px]">{row.item.sku}</span>
                ),
            cellTitle: (row) => (row.kind === 'model' ? undefined : row.item.sku),
        },
        {
            key: 'kind',
            label: 'Clasif.',
            note: 'Clasificación',
            width: '12%',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'variant' ? null : (
                    <KindChip
                        kind={catalogKindOf(
                            row.kind === 'model' ? row.group.defaultVariant : row.item,
                        )}
                    />
                ),
        },
        {
            key: 'price',
            label: 'Precio',
            width: '12.5%',
            numeric: true,
            sortKey: 'price',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'model'
                    ? priceRangeLabel(row.group)
                    : formatMXN(row.item.cashPriceCents / 100),
            cellClassName: () => 'tabular-nums',
        },
        {
            key: 'stock',
            label: 'Stock',
            width: '9%',
            numeric: true,
            sortKey: 'stock',
            cellOverflow: 'nowrap',
            // Como Inventario: la cifra, también el 0; un servicio no lleva inventario.
            cell: (row) => {
                const item = row.kind === 'model' ? modelSummaryRow(row) : row.item;
                return isService(item) ? PENDING_CELL : String(item.stock ?? 0);
            },
            cellTitle: (row) => {
                const item = row.kind === 'model' ? modelSummaryRow(row) : row.item;
                return isService(item) ? 'Servicio: no lleva inventario' : stockLabelOf(item);
            },
            cellClassName: (row) =>
                cn(
                    'font-bold tabular-nums',
                    STOCK_CLASS[
                        stockToneOf(row.kind === 'model' ? modelSummaryRow(row) : row.item)
                    ],
                ),
        },
        {
            key: 'alerts',
            label: 'Alertas',
            width: '14%',
            cell: (row) => (
                <AlertsCell row={row.kind === 'model' ? modelSummaryRow(row) : row.item} />
            ),
            cellTitle: (row) =>
                stockAlertOf(row.kind === 'model' ? modelSummaryRow(row) : row.item)?.text,
        },
        {
            key: 'financiers',
            label: 'Financ.',
            note: 'Financieras que aceptan el producto',
            width: '10.5%',
            cellOverflow: 'nowrap',
            cell: (row) => (
                <FinancierChips
                    codes={
                        row.kind === 'model'
                            ? groupFinanciers(row.group)
                            : row.item.eligibleFinanciers
                    }
                />
            ),
        },
        {
            key: 'promo',
            label: 'Promo',
            note: 'Promoción vigente',
            width: '11.5%',
            cell: (row) =>
                row.kind === 'model' ? (
                    <ModelPromotionCell row={row} />
                ) : (
                    <PromotionCell row={row.item} />
                ),
        },
    ];
}

/** Las píldoras con su conteo sobre lo cargado. «Lento» se omite: el catálogo no trae ventas por SKU. */
function statItems(
    stats: CatalogStats,
    scope: LoadedScope,
    isLoading: boolean,
): StatsBarItem<StatFilter>[] {
    return STAT_PILLS.map((pill) => ({
        key: pill.key,
        label: pill.label,
        tone: pill.tone,
        value: isLoading ? '…' : loadedCountLabel(stats[pill.key], scope),
        title: [pill.hint, loadedCountTitle(pill.label, scope, PRODUCTS_NOUN)]
            .filter(Boolean)
            .join(' · '),
    }));
}

export function CatalogTable({
    table,
    pageEntries,
    allRows,
    filteredCount,
    modelsCount,
    metrics,
    hasActiveFilters,
    stat,
    onStatChange,
    sort,
    onSortChange,
    selectedSku,
    onSelect,
    isLoading,
    errorCopy,
    onRetry,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    nextPageError,
}: {
    table: Table<CatalogEntry<PortalCatalogRow>>;
    /** Las entradas de la página que se está viendo: modelos de teléfono y SKU sueltos. */
    pageEntries: CatalogEntry<PortalCatalogRow>[];
    /** TODO lo cargado, sin filtrar: sobre eso cuentan las píldoras. */
    allRows: PortalCatalogRow[];
    /** Cuántas filas pasan los filtros sobre lo cargado. */
    filteredCount: number;
    /** «3 modelos · 7 variantes» de los teléfonos que pasan los filtros; `null` sin teléfonos. */
    modelsCount: string | null;
    metrics: CatalogMetrics;
    /** Discrimina el «la tienda no tiene equipos» del «los filtros no dejaron ninguno». */
    hasActiveFilters: boolean;
    stat: StatFilter;
    onStatChange: (next: StatFilter) => void;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    selectedSku: string | null;
    onSelect: (sku: string) => void;
    isLoading: boolean;
    /** La primera página no llegó: el motivo en lugar de la tabla. */
    errorCopy: CatalogListErrorCopy | null;
    onRetry: () => void;
    /** Quedan páginas en el servidor: nada de lo que se cuenta aquí es un total. */
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Por qué falló la ÚLTIMA página pedida; lo cargado sigue en la tabla. */
    nextPageError: string | null;
}) {
    const { expanded, toggle } = useExpandedModels(pageEntries, selectedSku);

    // Un catálogo que no se pudo leer no se pinta como tabla vacía: parecería que no hay equipos.
    if (errorCopy) {
        return (
            <ErrorState
                className="m-3"
                title={errorCopy.title}
                description={errorCopy.description}
                onRetry={errorCopy.canRetry ? onRetry : undefined}
            />
        );
    }

    const scope: LoadedScope = { loaded: allRows.length, hasMore: hasNextPage };
    const tableRows = modelTableRows(pageEntries, expanded);
    const stats = catalogStats(allRows);
    const countsNote = isLoading ? null : loadedCountsNote(scope, PRODUCTS_NOUN);

    return (
        <>
            <StatsBar
                items={statItems(stats, scope, isLoading)}
                active={stat}
                onChange={onStatChange}
                ariaLabel="Stock del catálogo"
            />

            {countsNote === null ? null : (
                <p
                    role="status"
                    className="flex-none border-b border-border bg-muted/40 px-3 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {countsNote}
                </p>
            )}

            <TableToolbar>
                <ToolbarMetric
                    label="Precio prom"
                    value={
                        metrics.avgPriceCents === null
                            ? PENDING_CELL
                            : formatMXN(metrics.avgPriceCents / 100)
                    }
                />
                <ToolbarMetric
                    label="Stock total"
                    value={metrics.stockTotal === null ? PENDING_CELL : String(metrics.stockTotal)}
                />
                <ToolbarMetric
                    label="Con SK"
                    tone="success"
                    value={
                        metrics.surekeepCount === null
                            ? PENDING_CELL
                            : `${String(metrics.surekeepCount)} de ${String(metrics.count)}`
                    }
                />
            </TableToolbar>

            <DataTable
                columns={catalogColumns(toggle)}
                rows={tableRows}
                rowKey={(row) => row.key}
                density="compact"
                sort={sort}
                onSortToggle={(key) => {
                    onSortChange(nextSort(sort, key));
                }}
                onRowClick={(row) => {
                    if (row.kind !== 'model') {
                        onSelect(row.item.sku);
                        return;
                    }
                    if (!row.expanded) toggle(row.key);
                    if (!row.group.variants.some((variant) => variant.sku === selectedSku)) {
                        onSelect(row.group.defaultVariant.sku);
                    }
                }}
                rowClassName={(row) => (row.kind === 'variant' ? VARIANT_ROW_CLASS : undefined)}
                isRowSelected={(row) => rowSku(row) !== null && rowSku(row) === selectedSku}
                isLoading={isLoading}
                empty={emptyNote({ hasFilters: hasActiveFilters || stat !== 'all', hasNextPage })}
            />

            <LoadedScopeFooter
                noun={PRODUCTS_NOUN}
                scope={isLoading ? null : scope}
                shown={isLoading ? null : filteredCount}
                table={table}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={onLoadMore}
                nextPageError={nextPageError}
                countDetail={isLoading ? null : modelsCount}
            />
        </>
    );
}

/** Qué decir sin filas: los filtros, lo que falta cargar o una tienda sin equipos. */
function emptyNote({ hasFilters, hasNextPage }: { hasFilters: boolean; hasNextPage: boolean }) {
    if (!hasFilters) return 'Esta tienda no tiene equipos cargados en el catálogo.';
    if (hasNextPage) {
        return 'Ningún producto cargado coincide con los filtros; usa «Cargar más» para traer el resto.';
    }
    return 'Ningún producto coincide con los filtros.';
}

function FinancierChips({ codes }: { codes: string[] }) {
    const chips = financierChips(codes);
    if (chips.length === 0) {
        return <span className="text-[10px] text-muted-foreground">{PENDING_CELL}</span>;
    }
    return (
        <span className="inline-flex gap-1">
            {chips.map((chip) => (
                <Badge
                    key={chip.code}
                    variant={chip.tone}
                    size="xs"
                    title={chip.name}
                    className="px-[5px] py-px text-[9px]"
                >
                    {chip.short}
                </Badge>
            ))}
        </span>
    );
}

function PromotionCell({ row }: { row: PortalCatalogRow }) {
    if (!hasPromotion(row)) {
        return <span className="text-[10px] text-muted-foreground">{PENDING_CELL}</span>;
    }
    return (
        <PromotionPill
            promotions={skuPromotionSummaries(row)}
            undetailedReason={undetailedPromotionReason(row)}
            showKind
            compact
        />
    );
}
